/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/ChangePasswordModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger } from "../../utils/logger.js";
import { apiMessageOrGeneric } from "../../utils/genericErrorMessages.js";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import styles from "./ChangePasswordModal.module.css";
import { IconButton, SvgIcon } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import OtpInput from "react-otp-input";

import { ReactComponent as VisibilityIcon } from "../../assets/icons/Visibility.svg";
import { ReactComponent as VisibilityOffIcon } from "../../assets/icons/Visibility_off.svg";

/** OTP input border when there is a code error */
const OTP_INPUT_BORDER_ERROR = "1px solid red";
/** OTP input border when there is no error */
const OTP_INPUT_BORDER_NORMAL = "1px solid #909092";
/** OTP input text color when there is a code error */
const OTP_INPUT_COLOR_ERROR = "red";
/** OTP input text color when there is no error */
const OTP_INPUT_COLOR_NORMAL = "black";
/** OTP input border radius */
const OTP_INPUT_BORDER_RADIUS = "8px";

/**
 * Modal for changing password (forgot-password flow).
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {string} [props.userEmail] - User email for context
 * @param {function} props.onClose - Callback when the modal is closed
 * @returns {JSX.Element}
 */
const ChangePasswordModal = ({ open, userEmail, onClose }) => {
  const dialogRef = useRef(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [confirmationCode, setConfirmationCode] = useState("");
  const [values, setValues] = useState({
    password: "",
    confirmPassword: "",
    showPassword: false,
    showConfirmPassword: false,
    errorMessage: "",
    showPasswordError: false,
    showCodeError: false,
    loading: false,
  });

  useEffect(() => {
    const handleClose = () => {
      setValues({
        ...values,
        showPasswordError: false,
        showCodeError: false,
        errorMessage: "",
      });
      onClose();
    };

    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleClose);
    }

    if (open) {
      dialog.showModal();
    } else if (dialog.open) {
      dialog.close();
    } else {
      // Dialog already matches the desired open state
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleClose);
      }
    };
  }, [open, onClose]);

  const handleInputChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value });
  };

  const handleClickShowPassword = (prop) => () => {
    setValues({ ...values, [prop]: !values[prop] });
  };

  const isPasswordValid = (password) => {
    if (password.length < 6 || password.length > 15) {
      return false;
    }

    // Check that we have both matches
    const regex = [/\d/, /[A-Z]/];
    let matches = 0;
    for (let r = 0; r < regex.length; r++) {
      if ((password.match(regex[r]) || []).length > 0) {
        matches++;
      }
    }
    return matches == 2;
  };

  const changePassword = async () => {
    if (values.password !== values.confirmPassword) {
      setValues({
        ...values,
        showPasswordError: true,
        showCodeError: false,
        errorMessage: "PASSWORDS_DO_NOT_MATCH",
      });
      return;
    }

    if (!isPasswordValid(values.password)) {
      setValues({
        ...values,
        showPasswordError: true,
        showCodeError: false,
        errorMessage: "INVALID_PASSWORD_FORMAT",
      });
      return;
    }

    setValues({
      ...values,
      loading: true,
      showPasswordError: false,
      showCodeError: false,
      errorMessage: "",
    });

    const authParams = {
      email: userEmail,
      confirmationCode: confirmationCode,
      password: values.password,
    };
    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(authParams),
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_ROOT_API}auth/password/confirm`,
        options,
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(apiMessageOrGeneric(data?.message));
      }

      setTimeout(() => {
        setValues({
          ...values,
          loading: false,
        });
        navigate("/passwordSuccess");
      }, 2000);
    } catch (error) {
      logger.info("error: ", { data: error });
      let message = "",
        errorType = "password";
      if (error.message === "UserNotFoundException") {
        message = "NO_ACCOUNT_ASSOCIATED_WITH_EMAIL";
      } else if (error.message === "LimitExceededException") {
        message = "ATTEMPTS_LIMIT_EXCEEDED";
      } else if (error.message === "ExpiredCodeException") {
        message = "VERIFICATION_CODE_EXPIRED";
        errorType = "confirmationCode";
      } else if (error.message === "InvalidPasswordException") {
        message = "PASSWORD_DOES_NOT_MEET_REQUIREMENTS";
      } else if (error.message === "CodeMismatchException") {
        message = "INVALID_VERIFICATION_CODE";
        errorType = "confirmationCode";
      } else {
        message = "ERROR_CHANGING_PASSWORD";
      }

      setTimeout(() => {
        setValues({
          ...values,
          loading: false,
          showPasswordError: errorType === "password" ? true : false,
          showCodeError: errorType === "confirmationCode" ? true : false,
          errorMessage: message,
        });
      }, 2000);
    }
  };

  const buttonDisabled = () => {
    return (
      values.loading ||
      values.password.length < 1 ||
      values.confirmPassword.length < 1 ||
      confirmationCode.length < 6
    );
  };

  return (
    <>
      {open && <div className={styles.overlay}></div>}
      <dialog ref={dialogRef} className={styles.modal}>
        <div className={styles.closeButtonContainer}>
          <button className={styles.closeButton} onClick={onClose}>
            &times;
          </button>
        </div>

        <div className={styles.modalHeader}>
          {values.loading ? (
            <>
              <span className={styles.title}>
                {t("pages.forgotPassword.verifyingCode")}
              </span>
              <CircularProgress />
            </>
          ) : (
            <>
              <span className={styles.title}>
                {t("pages.forgotPassword.verifyCodeSuccess")}
              </span>
              <span className={styles.subtitle}>
                {t("pages.forgotPassword.instructions01")}
                {userEmail}
                {t("pages.forgotPassword.instructions02")}
              </span>
            </>
          )}
        </div>

        <div className={styles.otpContainer}>
          <span className={styles.subtitle}>
            {t("pages.forgotPassword.verifyCode")}
          </span>
          <OtpInput
            value={confirmationCode}
            onChange={setConfirmationCode}
            containerStyle={styles.otpInputContainer}
            inputStyle={{
              width: "65px",
              height: "65px",
              fontSize: "24px",
              border: values.showCodeError
                ? OTP_INPUT_BORDER_ERROR
                : OTP_INPUT_BORDER_NORMAL,
              color: values.showCodeError
                ? OTP_INPUT_COLOR_ERROR
                : OTP_INPUT_COLOR_NORMAL,
              borderRadius: OTP_INPUT_BORDER_RADIUS,
            }}
            numInputs={6}
            renderInput={(props) => <input {...props} />}
          />
          {values.showCodeError && (
            <div className={styles.messageContainer}>
              <span className={styles.errorMessage}>
                {t(`errors.forgotPassword.${values.errorMessage}`)}
              </span>
            </div>
          )}
        </div>
        <div className={styles.passwordsContainer}>
          <span className={styles.subtitle}>
            {t("pages.forgotPassword.newPassword")}
          </span>
          <div className={styles.passwordContainer}>
            <label className={styles.subtitle}>
              {t("pages.forgotPassword.createNewPassword")}
            </label>
            <div className={styles.inputContainer}>
              <input
                type={values.showPassword ? "text" : "password"}
                onChange={handleInputChange("password")}
                value={values.password}
                style={{
                  border: values.showPasswordError
                    ? OTP_INPUT_BORDER_ERROR
                    : OTP_INPUT_BORDER_NORMAL,
                  color: values.showPasswordError
                    ? OTP_INPUT_COLOR_ERROR
                    : OTP_INPUT_COLOR_NORMAL,
                }}
              />
              <IconButton
                onClick={handleClickShowPassword("showPassword")}
                style={{ position: "absolute", top: "0", right: "0" }}
              >
                {values.showPassword ? (
                  <SvgIcon
                    sx={{
                      color: values.showPasswordError
                        ? OTP_INPUT_COLOR_ERROR
                        : OTP_INPUT_COLOR_NORMAL,
                    }}
                    component={VisibilityOffIcon}
                  />
                ) : (
                  <SvgIcon
                    sx={{
                      color: values.showPasswordError
                        ? OTP_INPUT_COLOR_ERROR
                        : OTP_INPUT_COLOR_NORMAL,
                    }}
                    component={VisibilityIcon}
                  />
                )}
              </IconButton>
            </div>
          </div>
          <div className={styles.passwordRequirements}>
            <span className={styles.subtitle}>
              {t("pages.forgotPassword.requirementInstructions")}
            </span>
            <ul className={styles.list}>
              <li className={styles.listItem}>
                {t("pages.forgotPassword.requirements01")}
              </li>
              <li className={styles.listItem}>
                {t("pages.forgotPassword.requirements02")}
              </li>
            </ul>
          </div>
          <div className={styles.passwordContainer}>
            <label className={styles.subtitle}>
              {t("pages.forgotPassword.confirmPassword")}
            </label>
            <div className={styles.inputContainer}>
              <input
                type={values.showConfirmPassword ? "text" : "password"}
                onChange={handleInputChange("confirmPassword")}
                value={values.confirmPassword}
                style={{
                  border: values.showPasswordError
                    ? OTP_INPUT_BORDER_ERROR
                    : OTP_INPUT_BORDER_NORMAL,
                  color: values.showPasswordError
                    ? OTP_INPUT_COLOR_ERROR
                    : OTP_INPUT_COLOR_NORMAL,
                }}
              />
              <IconButton
                onClick={handleClickShowPassword("showConfirmPassword")}
                style={{ position: "absolute", top: "0", right: "0" }}
              >
                {values.showConfirmPassword ? (
                  <SvgIcon
                    sx={{
                      color: values.showPasswordError
                        ? OTP_INPUT_COLOR_ERROR
                        : OTP_INPUT_COLOR_NORMAL,
                    }}
                    component={VisibilityOffIcon}
                  />
                ) : (
                  <SvgIcon
                    sx={{
                      color: values.showPasswordError
                        ? OTP_INPUT_COLOR_ERROR
                        : OTP_INPUT_COLOR_NORMAL,
                    }}
                    component={VisibilityIcon}
                  />
                )}
              </IconButton>
            </div>
          </div>
          {values.showPasswordError && (
            <div className={styles.messageContainer}>
              <span className={styles.errorMessage}>
                {t(`errors.forgotPassword.${values.errorMessage}`)}
              </span>
            </div>
          )}
        </div>
        <div className={styles.modalButtons}>
          <button className={styles.cancelButton} onClick={onClose}>
            {t("pages.forgotPassword.cancelButton")}
          </button>
          <button
            className={styles.confirmButton}
            onClick={changePassword}
            disabled={buttonDisabled()}
          >
            {t("pages.forgotPassword.confirmNewPasswordButton")}
          </button>
        </div>
      </dialog>
    </>
  );
};

export default ChangePasswordModal;
